import { cn, formatDate, formatDueNote, printPercent1FD } from "logic/utils";
import Link from "next/link";
import { AmountTooltip, Badge, Icon, IconButton, Ty } from "ui/components";

import { getLoanApr } from "./data/loanApr";
import type { Loan } from "./data/types";
import { useUILoanStatus } from "./LoanStatusTag";
import { useNow } from "./NowProvider";
import { isQuoteQuarantined } from "./quarantinedQuotes";
import { FALLBACK_STATUS_BADGE, type LoanRowData, STATUS_BADGE, STATUS_NOTE_COLOR } from "./types";
import { useLoanTimestamps } from "./useLoanTimestamp";
import { useRepayInfo } from "./useRepayInfo";


/**
 * Row background plus the hover state layer from Figma 3540:6390 ("interactions").
 * The two states must not leak into each other:
 *  - `:not(:has([data-chevron]:hover))` keeps hovering the chevron from lighting
 *    up the row, which a plain `hover:` gets wrong since it is a descendant.
 *  - `isolate` + `-z-10` keeps the tint *under* the row's content, so it can't
 *    wash over the chevron and read as a button hover. In frame 3540:6497 the
 *    hovered row is #f6f3ef while the button stays #fcfbf9.
 */
const ROW_SHELL = `
  relative isolate overflow-clip rounded-button border border-sys-outline-outline bg-surface-surface
  outline-hidden
  before:absolute before:inset-0 before:-z-10 before:content-['']
  [&:hover:not(:has([data-chevron]:hover))]:before:bg-state-layers-tertiary-opacity-8
`;

/**
 * The seven-column layout only works at its designed 1040px column width
 * (225 + 5x151.8 + 56); any narrower and the cells overlap. 1080px is that
 * column plus the 2x20px page padding, so the table appears exactly when it
 * fits and the cards cover everything below.
 */
export const TABLE_VISIBLE = "hidden min-[1080px]:flex";
const CARDS_VISIBLE = "flex min-[1080px]:hidden";

type Props = {
  loan: Loan;
};

export function LoanRow(props: Props) {
  const { loan } = props;

  // Per-loan hooks, called here rather than in the layouts so both presentations
  // read the same values. Mirrors the old table's `ActiveLoanRow`.
  const status = useUILoanStatus(loan);
  const { isRepayment } = useLoanTimestamps(loan);
  const now = useNow();
  const { data } = useRepayInfo(loan, isRepayment);

  // Same three-state contract as the loan detail page: `null` renders `--`,
  // `undefined` renders a skeleton. Kept distinct with `&&` — `??` would
  // collapse the quarantined case into the loading one.
  const quote = isQuoteQuarantined(loan) ? null : data;

  const row: LoanRowData = {
    collateral: loan.metadata.name ?? "-",
    status,
    principal: <AmountTooltip amount={loan.loanState.scaledBalance} decimals={18} />,
    apr: printPercent1FD(getLoanApr(loan)),
    dueDate: formatDate(loan.nextDeadline),
    dueNote: formatDueNote(loan.nextDeadline, now),
    amount: quote && <AmountTooltip amount={quote.total} decimals={loan.erc20.decimals} />,
  };

  // Both the row and its chevron open the existing loan detail view, which
  // still lives on the old route.
  const href = `/loans?loan=${loan.loanTermsHash}`;

  return (
    <>
      <DesktopRow className={TABLE_VISIBLE} href={href} loan={row} />
      <MobileRow className={CARDS_VISIBLE} href={href} loan={row} />
    </>
  );
}

type LayoutProps = {
  loan: LoanRowData;
  href: string;
  className?: string;
};

function DesktopRow(props: LayoutProps) {
  const { loan, href, className } = props;

  return (
    <Link className={cn(ROW_SHELL, "items-center", className)} href={href}>
      {/* Collateral is the one fixed column; the middle five share the rest equally. */}
      <div className="w-[225px] shrink-0 px-4 py-4">
        <p className="text-title-sm-400 truncate text-surface-on-surface">{loan.collateral}</p>
      </div>

      <div className="min-w-0 flex-1 px-4 py-4">
        <Badge size="md" variant={STATUS_BADGE[loan.status] ?? FALLBACK_STATUS_BADGE}>
          {loan.status}
        </Badge>
      </div>

      <div className="min-w-0 flex-1 px-4 py-4">
        <div className="text-body-sm-400 whitespace-nowrap text-surface-on-surface">{loan.principal}</div>
      </div>

      <div className="min-w-0 flex-1 px-4 py-4">
        <p className="text-body-sm-400 whitespace-nowrap text-surface-on-surface">{loan.apr}</p>
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-0.5 px-4 py-4 whitespace-nowrap">
        <p className="text-body-sm-400 text-surface-on-surface">{loan.dueDate}</p>
        <p className={cn("text-label-sm-400", STATUS_NOTE_COLOR[loan.status] ?? "text-surface-on-surface-subtle")}>
          {loan.dueNote}
        </p>
      </div>

      <div className="min-w-0 flex-1 px-4 py-4">
        <Ty className="text-body-sm-400 whitespace-nowrap text-surface-on-surface" value={loan.amount} />
      </div>

      <div className="shrink-0 py-4 pr-4 pl-2">
        {/* A span, not a button: the whole row is already the link, and an
            anchor cannot nest inside another. Keeping the IconButton classes
            preserves its own hover and pressed states, and `data-chevron` is
            what stops those from also tinting the row.
            The outline variant ships no fill; Figma gives this tile its own
            surface so the row's hover tint never bleeds through it. */}
        <IconButton aria-hidden as="span" className="bg-surface-surface" data-chevron size="sm" variant="outline">
          <Icon name="caret_right" />
        </IconButton>
      </div>
    </Link>
  );
}

function MobileRow(props: LayoutProps) {
  const { loan, href, className } = props;

  return (
    <Link className={cn(ROW_SHELL, "items-center gap-4 p-4", className)} href={href}>
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <div className="text-label-xl-400 flex items-center gap-4 text-surface-on-surface">
          <p className="min-w-0 flex-1 truncate">{loan.collateral}</p>

          {/* Floored width so a loading skeleton can't collapse to nothing and
              hand its space to the collateral name, shifting the card's layout
              between load and loaded. */}
          <Ty className="w-24 shrink-0 text-right whitespace-nowrap" value={loan.amount} />
        </div>

        <div className="flex items-center justify-between gap-4">
          <Badge size="md" variant={STATUS_BADGE[loan.status] ?? FALLBACK_STATUS_BADGE}>
            {loan.status}
          </Badge>

          <p
            className={cn(
              "text-body-sm-400 whitespace-nowrap",
              STATUS_NOTE_COLOR[loan.status] ?? "text-surface-on-surface-variant",
            )}
          >
            {loan.dueNote}
          </p>
        </div>
      </div>

      {/* Mobile drops the button chrome — a bare 16px chevron, per Figma. Carries
          `data-chevron` even though it has no hover styling of its own: as a
          `button` it already suppressed the row tint via `:has(button:hover)`,
          and dropping the attribute would silently start tinting the row. */}
      <span aria-hidden className="shrink-0 text-surface-on-surface" data-chevron>
        <Icon name="caret_right" size="small" />
      </span>
    </Link>
  );
}
